import { isString, trim, upperFirst } from 'lodash';
import * as i0 from "@angular/core";
export var LogLevel;
(function (LogLevel) {
    LogLevel["Info"] = "info";
    LogLevel["Log"] = "log";
    LogLevel["Warn"] = "warn";
    LogLevel["Error"] = "error";
    LogLevel["Debug"] = "debug";
    // Trace = 'trace'
})(LogLevel || (LogLevel = {}));
export class LoggingService {
    constructor() {
        this.defaultTag = 'SSO_TRACE';
        this.appSource = 'SSO Login OTK v2';
        this.tag = null;
    }
    // Check if New Relic is available
    get isNewRelicAvailable() {
        return typeof window !== 'undefined' && !!window.newrelic;
    }
    // Check if we should log to console
    get shouldConsoleLog() {
        return typeof window === 'undefined' || !window.newrelic || !window.APP_SETTINGS || window.APP_SETTINGS.ENV === 'development';
    }
    // adding the query param 'log' to our entry points allows verbose logging
    // this param can be added to the SSO launch link's redirect_uri, provider support can assist onsite IT
    // you can also copy the SSO launch link and paste in a new tab with the log param of your choice appended
    init(params) {
        if (params?.log === true || params?.log === 'true') {
            // bool - default tag
            this.tag = `[${this.defaultTag}]`;
        }
        else if (isString(params?.log)) {
            // string - use the string in the log
            this.tag = params?.log?.length > 0 ? `[${this.defaultTag}: ${params?.log}]` : `[${this.defaultTag}]`;
        }
        else {
            // lonely, empty param - default tag
            this.tag = window.document.location.href.match(/(\&|\?)log/) ? `[${this.defaultTag}]` : null;
        }
        // Log initialization for debugging
        if (this.tag) {
            this.info('Logging initialized', { logLevel: this.tag });
        }
    }
    /**
     * Standard log message
     */
    log(message, payload) {
        if (this.tag !== null) {
            const logMessage = `[SSO Log] ${this.tag} ${message}`;
            if (this.shouldConsoleLog) {
                console.log(logMessage, payload);
            }
            this.logToNewRelic(LogLevel.Log, logMessage, payload);
        }
    }
    /**
     * Information logging - consider allowing this without tag
     */
    info(message, payload) {
        if (this.tag !== null) {
            const logMessage = `[SSO Info] ${this.tag} ${message}`;
            if (this.shouldConsoleLog) {
                console.info(logMessage, payload);
            }
            this.logToNewRelic(LogLevel.Info, logMessage, payload);
        }
    }
    /**
     * Debug level logging
     */
    debug(message, payload) {
        if (this.tag !== null) {
            const logMessage = `[SSO Debug] ${this.tag} ${message}`;
            if (this.shouldConsoleLog) {
                console.debug(logMessage, payload);
            }
            this.logToNewRelic(LogLevel.Debug, logMessage, payload);
        }
    }
    /**
     * Warning level logging
     */
    warn(message, payload) {
        if (this.tag !== null) {
            const logMessage = `[SSO Warn] ${this.tag} ${message}`;
            if (this.shouldConsoleLog) {
                console.warn(logMessage, payload);
            }
            this.logToNewRelic(LogLevel.Warn, logMessage, payload);
        }
    }
    /**
     * Error logging - always logs regardless of tag
     */
    error(message, payload, error) {
        const logMessage = `[SSO Error] ${this.tag ? `${this.tag} ` : ''}${message}`;
        if (this.shouldConsoleLog) {
            console.error(logMessage, payload);
        }
        // If an Error object was provided, use it directly
        if (error instanceof Error) {
            this.logToNewRelic(LogLevel.Error, logMessage, payload, error);
        }
        else {
            this.logToNewRelic(LogLevel.Error, logMessage, payload);
        }
    }
    /**
     * Send logs to New Relic
     */
    logToNewRelic(level, message, payload, error) {
        // Check if `newrelic` is available on the window object
        if (!this.isNewRelicAvailable)
            return;
        try {
            // Get stack trace but skip this function call
            const stack = this.getStackTrace();
            // Safely stringify payload
            let details = '';
            try {
                details = payload ? this.safeStringify(payload) : '';
            }
            catch (e) {
                details = `[Failed to stringify payload: ${e}]`;
            }
            const body = {
                message: message || '',
                severity: level,
                source: this.appSource,
                timestamp: new Date().toISOString(),
                payload: payload,
                details: details
            };
            // Log to New Relic with appropriate level
            window.newrelic.log(body.message, {
                level: level,
                customAttributes: {
                    severity: upperFirst(level),
                    source: body.source,
                    timestamp: body.timestamp,
                    details: body.details,
                    stack: stack.join('\n')
                }
            });
            // For errors, also use the noticeError method to enable error tracking
            if (level === LogLevel.Error) {
                const errorObj = error ?? new Error(message);
                window.newrelic.noticeError(errorObj, {
                    customAttributes: {
                        severity: upperFirst(level),
                        source: body.source,
                        details: body.details
                    }
                });
            }
        }
        catch (e) {
            // If New Relic logging fails, log to console
            console.error('Error sending logs to New Relic:', e);
        }
    }
    /**
     * Safely get stack trace
     */
    getStackTrace() {
        try {
            return (new Error().stack ?? '')
                .split('\n')
                .map(line => trim(line))
                .slice(3); // Remove error message and this function's calls
        }
        catch (e) {
            return ['[Failed to get stack trace]'];
        }
    }
    /**
     * Safely stringify objects with circular references
     */
    safeStringify(obj, maxDepth = 3) {
        // Handle circular references
        const seen = new WeakSet();
        return JSON.stringify(obj, (key, value) => {
            if (maxDepth <= 0) {
                return typeof value === 'object' ? '[Object]' : value;
            }
            if (typeof value === 'object' && value !== null) {
                if (seen.has(value)) {
                    return '[Circular]';
                }
                seen.add(value);
                return value;
            }
            return value;
        });
    }
    static { this.ɵfac = function LoggingService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || LoggingService)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: LoggingService, factory: LoggingService.ɵfac, providedIn: 'root' }); }
}
